import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { log } from '../lib/logger';

export function useStripeConnect() {
  const business = useAuthStore(s => s.business);
  const businessId = business?.id;
  const mountedRef = useRef(true);

  const [isConnected, setIsConnected] = useState(false);
  const [chargesEnabled, setChargesEnabled] = useState(false);
  const [payoutsEnabled, setPayoutsEnabled] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(false);
  // Stripe Direct Charges Spec v1 P4: 'legacy_express' accounts are prompted to
  // reconnect onto a Stripe-liable v2 account; reconnectPending = replacement
  // exists but isn't charges-enabled yet (check-connect-status swaps it in).
  const [accountModel, setAccountModel] = useState<string | null>(null);
  const [reconnectPending, setReconnectPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build 45 sweep: parity with mobile useStripeConnect. Canadian Express
  // accounts can flip chargesEnabled + payoutsEnabled true while
  // `details_submitted` stays false (pending ancillary verification docs),
  // which previously false-nagged connected accounts with a "finish setup"
  // banner. A Connect account that can charge AND pay out is functional.
  const isFullyReady = chargesEnabled && payoutsEnabled;

  const refreshStatus = useCallback(async () => {
    if (!businessId) { setStatusLoading(false); return; }
    setStatusLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('check-connect-status', {
        body: { business_id: businessId },
      });
      if (!mountedRef.current) return;
      if (error || data?.error) { setStatusLoading(false); return; }

      setIsConnected(data.status !== 'not_started');
      setChargesEnabled(data.charges_enabled ?? false);
      setPayoutsEnabled(data.payouts_enabled ?? false);
      setIsOnboarded(data.status === 'complete');
      setAccountModel(data.account_model ?? null);
      setReconnectPending(!!data.reconnect_pending);
    } catch { /* silent */ }
    finally { if (mountedRef.current) setStatusLoading(false); }
  }, [businessId]);

  useEffect(() => {
    mountedRef.current = true;
    if (business?.stripe_connect_account_id) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      refreshStatus();
    } else {
      setStatusLoading(false);
    }
    return () => { mountedRef.current = false; };
  }, [business?.stripe_connect_account_id, refreshStatus]);

  // Listen for popup completion from /connect-complete. The popup posts
  // { type: 'complete', business_id } on BroadcastChannel('stripe-connect')
  // and then self-closes. Match on business_id to avoid cross-tenant bleed
  // if the same origin has multiple admin tabs open.
  useEffect(() => {
    if (!businessId) return;
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('stripe-connect');
    } catch {
      // BroadcastChannel unsupported in very old browsers — opener refreshes
      // on next visible-tab refocus via the existing on-mount effect.
      return;
    }
    const handler = (e: MessageEvent) => {
      const data = e.data as { type?: string; business_id?: string } | null;
      if (data?.type === 'complete' && data.business_id === businessId) {
        void refreshStatus();
      }
    };
    channel.addEventListener('message', handler);
    return () => {
      channel?.removeEventListener('message', handler);
      channel?.close();
    };
  }, [businessId, refreshStatus]);

  const startOnboarding = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setPopupBlocked(false);
    setError(null);

    // Open blank tab SYNCHRONOUSLY inside the user gesture, before any await.
    // This is the critical fix: browsers require window.open to run in the
    // same tick as the click, or they block it as a script-initiated popup.
    const popupWindow = window.open('about:blank', '_blank');

    if (!popupWindow) {
      if (mountedRef.current) {
        setPopupBlocked(true);
        setLoading(false);
      }
      return;
    }

    try {
      popupWindow.document.title = 'Taking you to Stripe...';
      popupWindow.document.body.innerText = 'Taking you to Stripe...';
    } catch {
      // Some browsers disallow writing to about:blank; placeholder is optional.
    }

    try {
      const { data, error } = await supabase.functions.invoke('create-connect-account', {
        body: { business_id: businessId },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error('No onboarding URL returned');
      popupWindow.location.href = data.url;
    } catch (e) {
      try { popupWindow.close(); } catch { /* ignore */ }
      log.error('Stripe Connect onboarding failed:', e);
      if (mountedRef.current) {
        const msg = e instanceof Error ? e.message : 'Failed to start Stripe Connect onboarding';
        setError(msg);
      }
    } finally { if (mountedRef.current) setLoading(false); }
  }, [businessId]);

  const retryInCurrentTab = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    setPopupBlocked(false);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke('create-connect-account', {
        body: { business_id: businessId },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      if (!data?.url) throw new Error('No onboarding URL returned');
      window.location.href = data.url;
    } catch (e) {
      log.error('Stripe Connect onboarding failed:', e);
      if (mountedRef.current) {
        const msg = e instanceof Error ? e.message : 'Failed to start Stripe Connect onboarding';
        setError(msg);
        setLoading(false);
      }
    }
  }, [businessId]);

  return {
    isConnected,
    isOnboarded,
    chargesEnabled,
    payoutsEnabled,
    isFullyReady,
    accountModel,
    reconnectPending,
    needsReconnect: accountModel === 'legacy_express',
    loading,
    statusLoading,
    popupBlocked,
    error,
    startOnboarding,
    retryInCurrentTab,
    refreshStatus,
  };
}
